import _ from 'underscore';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { SimpleSummaryBar } from '@bingads-webui-campaign-react/summary-bar';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { withScope } from '@bingads-webui-react/scope-service';

const CampaignODataCallForCustomerLevel = (props) => {
  const {
    selSummaryBarFallback,
    scope,
    selectedParent,
    breadCrumbText,
    backButtonText,
    backButtonUrl,
    breadcrumbUrl,
    ...others
  } = props;

  return (
    <CampaignOData
      call={
          // eslint-disable-next-line arrow-body-style
          ({ currentCustomer }) => {
            // TODO: if custom list limit > 100, we will need to write code to fix this.
            // Known issue: if > 20 lists, $top:20 query param will break this functionality.
            // Workaround: supply top param with a value > 20.
            return currentCustomer.PlacementExclusionLists.get({ top: 100 });
          }
      }
      fallback={selSummaryBarFallback}
      // If the list name call fails, show empty string.
      onError={() => selSummaryBarFallback}
    >
      {
        (response) => {
          const predicate = item => item.Id === scope.values.selListId;

          if (response.value && _.find(response.value, predicate)) {
            const list = _.find(response.value, predicate);
            const additionalItems = [];
            additionalItems.push({
              key: `navigate-selListId-${list.Id}`,
              text: list.Name,
            });

            return (
              <SimpleSummaryBar
                selectedParent={selectedParent}
                breadCrumbText={breadCrumbText}
                backButtonText={backButtonText}
                backButtonUrl={backButtonUrl}
                breadcrumbUrl={breadcrumbUrl}
                additionalItems={additionalItems}
                {...others}
              />
            );
          }

          return selSummaryBarFallback;
        }
      }
    </CampaignOData>
  );
};

CampaignODataCallForCustomerLevel.propTypes = {
  selSummaryBarFallback: PropTypes.element.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      selListId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  breadCrumbText: PropTypes.string,
  backButtonText: PropTypes.string,
  backButtonUrl: PropTypes.string,
  breadcrumbUrl: PropTypes.string.isRequired,
};

CampaignODataCallForCustomerLevel.defaultProps = {
  breadCrumbText: null,
  backButtonText: null,
  backButtonUrl: null,
};

const CampaignODataCallForAccountLevel = (props) => {
  const {
    selSummaryBarFallback,
    scope,
    selectedParent,
    breadCrumbText,
    backButtonText,
    backButtonUrl,
    breadcrumbUrl,
    ...others
  } = props;

  return (
    <CampaignOData
      call={
          // eslint-disable-next-line arrow-body-style
          ({ currentAccount }) => {
            // Get the association between this account and this list to display the list name.
            // This is to verify the account is associated before showing the list name.
            return currentAccount.PlacementExclusionLists.$withKey(scope.values.selListId)['Default.GetAccountAssociation'].$makeCall({
              payload: {},
            });
          }
      }
      fallback={selSummaryBarFallback}
      // If the list name call fails, show empty string.
      onError={() => selSummaryBarFallback}
    >
      {
        (response) => {
          if (response.value && response.value.length === 1) {
            const association = response.value[0];
            const additionalItems = [];
            additionalItems.push({
              key: `navigate-selListId-${association.SharedEntityId}`,
              text: association.SharedEntityName,
            });

            return (
              <SimpleSummaryBar
                selectedParent={selectedParent}
                breadCrumbText={breadCrumbText}
                backButtonText={backButtonText}
                backButtonUrl={backButtonUrl}
                breadcrumbUrl={breadcrumbUrl}
                additionalItems={additionalItems}
                {...others}
              />
            );
          }

          return selSummaryBarFallback;
        }
      }
    </CampaignOData>
  );
};

CampaignODataCallForAccountLevel.propTypes = {
  selSummaryBarFallback: PropTypes.element.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      selListId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  breadCrumbText: PropTypes.string,
  backButtonText: PropTypes.string,
  backButtonUrl: PropTypes.string,
  breadcrumbUrl: PropTypes.string.isRequired,
};

CampaignODataCallForAccountLevel.defaultProps = {
  breadCrumbText: null,
  backButtonText: null,
  backButtonUrl: null,
};

export const Loader = (props) => {
  const {
    siteMap,
    breadCrumbText,
    backButtonText,
    backButtonUrl,
    updateAvailability,
    scope,
    ...others
  } = props;

  const selectedParent = _.findWhere(siteMap, { selected: true });

  useEffect(() => {
    updateAvailability(!_.isEmpty(selectedParent));
  });

  const selSummaryBarFallback = (
    <SimpleSummaryBar
      selectedParent={selectedParent}
      breadCrumbText={breadCrumbText}
      backButtonText={backButtonText}
      backButtonUrl={backButtonUrl}
      {...others}
    />
  );

  // Add Site Exclusion List breadcrumb.
  // The parent ID is shared by the account-level and customer-level entrypoints in the sitemap.
  if (scope.values.selListId && selectedParent && selectedParent.id === 'site-exclusion-lists-entrypoint') {
    let queryString = '';
    let breadcrumbUrl = '';

    // Navigated to page via account-level entrypoint
    if (scope.values.aid) {
      queryString = `?uid=${scope.values.uid}&cid=${scope.values.cid}&aid=${scope.values.aid}`;
      breadcrumbUrl = `/campaign/vnext/${selectedParent.route}${queryString}`;

      return CampaignODataCallForAccountLevel({
        selSummaryBarFallback, scope, selectedParent, breadCrumbText, backButtonText, backButtonUrl, breadcrumbUrl, ...others,
      });
    }

    // Otherwise navigated to page via customer-level entrypoint
    queryString = `?uid=${scope.values.uid}&cid=${scope.values.cid}`;
    breadcrumbUrl = `/campaign/vnext/${selectedParent.route}${queryString}`;

    return CampaignODataCallForCustomerLevel({
      selSummaryBarFallback, scope, selectedParent, breadCrumbText, backButtonText, backButtonUrl, breadcrumbUrl, ...others,
    });
  }

  return selectedParent ? selSummaryBarFallback : null;
};

Loader.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
  breadCrumbText: PropTypes.string,
  backButtonText: PropTypes.string,
  backButtonUrl: PropTypes.string,
  updateAvailability: PropTypes.func,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      selListId: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

Loader.defaultProps = {
  breadCrumbText: null,
  backButtonText: null,
  backButtonUrl: null,
  updateAvailability: _.noop,
};

// This is the loader for the summary bar used by the linked pages in the SEL feature.
// The linked pages are the "page 2s" of the customer-level and account-level scopes.
// Those include the customer-level list-edit page and the account-level view-exclusions page.
export const SiteExclusionListLinkedPageLoader = withScope(withRouter(React.memo(Loader)));
